import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';
import { createLogger } from '../utils/logger';

const log = createLogger('TableForceExtension');

/**
 * TableForceExtension - 強制的にリサイズハンドルを生成・表示
 * TipTap Tableが正常に動作しない場合の緊急対応
 */
export const TableForceExtension = Extension.create({
  name: 'tableForce',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('tableForce'),
        view: (editorView) => {
          log.debug('🚀 TableForceExtension: Force initializing table resize handles');

          const forcedHandleSelector =
            '.column-resize-handle[data-markdown-wysiwyg-force-resize="true"]';
          const handleCreationTimers = new Set<number>();
          let activeDrag: {
            cell: HTMLElement;
            startX: number;
            startWidth: number;
          } | null = null;

          const handleMouseMove = (e: MouseEvent) => {
            if (!activeDrag) return;
            const diffX = e.clientX - activeDrag.startX;
            const newWidth = Math.max(50, activeDrag.startWidth + diffX);
            activeDrag.cell.style.width = `${newWidth}px`;
            log.debug(`📏 TableForceExtension: Resizing to ${newWidth}px`);
          };

          const handleMouseUp = () => {
            if (!activeDrag) return;
            activeDrag = null;
            document.body.style.cursor = '';
            log.debug('🖱️ TableForceExtension: Stopped dragging');
          };

          document.addEventListener('mousemove', handleMouseMove);
          document.addEventListener('mouseup', handleMouseUp);

          const forceCreateResizeHandles = () => {
            const tables = editorView.dom.querySelectorAll('table');
            log.debug(`🔧 TableForceExtension: Found ${tables.length} tables`);

            tables.forEach((table, tableIndex) => {
              const cells = table.querySelectorAll('th, td');
              log.debug(`📊 Table ${tableIndex}: Found ${cells.length} cells`);

              cells.forEach((cell, cellIndex) => {
                // 既存のハンドルがあるかチェック
                const existingHandle = cell.querySelector('.column-resize-handle');
                if (!existingHandle) {
                  // 強制的にリサイズハンドルを作成
                  const handle = document.createElement('div');
                  handle.className = 'column-resize-handle';
                  handle.dataset.markdownWysiwygForceResize = 'true';
                  handle.style.cssText = `
                    position: absolute;
                    right: -2px;
                    top: 0;
                    bottom: 0;
                    width: 8px;
                    background: #3b82f6;
                    cursor: col-resize;
                    z-index: 10;
                    pointer-events: auto;
                  `;

                  // セルの位置を相対位置に設定
                  (cell as HTMLElement).style.position = 'relative';
                  cell.appendChild(handle);

                  log.debug(
                    `✅ TableForceExtension: Created handle for table ${tableIndex}, cell ${cellIndex}`,
                  );

                  handle.addEventListener('mousedown', (e: MouseEvent) => {
                    activeDrag = {
                      cell: cell as HTMLElement,
                      startX: e.clientX,
                      startWidth: (cell as HTMLElement).offsetWidth,
                    };
                    document.body.style.cursor = 'col-resize';
                    log.debug('🖱️ TableForceExtension: Started dragging');
                    e.preventDefault();
                  });
                }
              });
            });
          };

          const scheduleForceCreateResizeHandles = (delay: number) => {
            const timerId = window.setTimeout(() => {
              handleCreationTimers.delete(timerId);
              forceCreateResizeHandles();
            }, delay);
            handleCreationTimers.add(timerId);
          };

          // 初期処理
          scheduleForceCreateResizeHandles(100);

          // DOM変更を監視して新しいテーブルにもハンドルを追加
          const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
              if (mutation.type !== 'childList') {
                continue;
              }

              for (const node of mutation.addedNodes) {
                if (node.nodeType !== Node.ELEMENT_NODE) {
                  continue;
                }

                const element = node as Element;
                if (element.tagName === 'TABLE' || element.querySelector('table')) {
                  log.debug('🆕 TableForceExtension: New table detected, creating handles');
                  scheduleForceCreateResizeHandles(50);
                }
              }
            }
          });

          observer.observe(editorView.dom, {
            childList: true,
            subtree: true,
          });

          return {
            destroy() {
              observer.disconnect();
              for (const timerId of handleCreationTimers) {
                window.clearTimeout(timerId);
              }
              handleCreationTimers.clear();
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
              activeDrag = null;
              document.body.style.cursor = '';
              for (const handle of editorView.dom.querySelectorAll(forcedHandleSelector)) {
                handle.remove();
              }
              log.debug('🚀 TableForceExtension: Cleaned up');
            },
          };
        },
      }),
    ];
  },
});
